/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Avatar, Badge, Box, Tooltip, Typography } from '@mui/material'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { AxiosError } from 'axios'
import { MButton, ProgressLoaders, UnregisterConfirmDialog } from 'components'
import LinkAccount, { LinkAccountMode } from 'components/ui/Dialog/LinkConfirmDialog'
import { UnlinkConfirmDialog } from 'components/ui/Dialog/UnlinkConfirmDialog'
import { CLIENT, ROUTES_PATH, isFeatureActive } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { APIs } from 'services'

interface IAccountProfile {
  onLinkedAccountsChange?: () => void
}

export const AccountProfile = ({ onLinkedAccountsChange }: IAccountProfile): JSX.Element => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { account, authToken, unregisterAccount, unregisterICAccount, getICStatus, getAllLinkedAccounts, getUser } = useAuth()
  const email = searchParams.get('email') ?? undefined
  const { enqueueSnackbar } = useSnackbar()
  const [isUnregisterDialogOpen, setIsUnregisterDialogOpen] = useState(false)
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false)
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false)
  const [accountNumber, setAccountNumber] = useState('')
  const [isVerified, setIsVerified] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [linkedAccounts, setLinkedAccounts] = useState<any[]>([])
  const isWEBclient = process.env.REACT_APP_CLIENT_NAME === CLIENT.WEB
  const ccsLink = isFeatureActive('css_link')
  const ccsUnlink = isFeatureActive('css_unlink')
  const unregisterAccountFeature = isFeatureActive('unregister_account')
  const isInvoiceCloudEnabled = process.env.REACT_APP_CLIENT_NAME !== CLIENT.WEB
  const emailCorrect = isWEBclient
    ? account?.ccsEmail?.toLowerCase() === account?.cognitoEmail?.toLowerCase()
    : account?.ccsEmail?.toLowerCase() === account?.icEmail?.toLowerCase() && account?.ccsEmail?.toLowerCase() === account?.cognitoEmail?.toLowerCase()
  const hasErrors = !isVerified || !emailCorrect || Boolean(account?.errorMessage)

  if (account?.errorMessage && !account?.ccsEmail) {
    enqueueSnackbar(account?.errorMessage || 'Something went wrong. Please try again later.', {
      variant: 'error',
      autoHideDuration: 5000
    })
    navigate(ROUTES_PATH.DASHBOARD)
  }

  const customerLoginURL = useMemo(() => {
    return `${process.env.REACT_APP_AWS_COGNITO_REDIRECT_SIGNIN
      }?email=${encodeURIComponent(account?.cognitoEmail)}&token=${authToken}`
  }, [authToken, account?.cognitoEmail])

  const handleUnregister = async (): Promise<void> => {
    try {
      if (account?.cognitoEmail) {
        setIsLoading(true)
        await unregisterAccount({
          email: account.cognitoEmail
        })

        await APIs.updateInfo({
          accountId: account?.accountId ?? '',
          AccessToken: authToken,
          email: account?.ccsEmail,
          admin: true,
          addRemoveAcctUpdates: [
            {
              accountPropertyManager: account?.accountId ?? '',
              accountPropertyOwner: account?.accountId ?? '',
              addRemoveAcctFlag: 'R'
            }
          ]
        }, authToken)

        enqueueSnackbar('Account unregistered successfully', { variant: 'success' })
        setTimeout(() => {
          navigate(ROUTES_PATH.ACCOUNT_MANAGER)
        }, 3000)
      } else enqueueSnackbar('Account not found', { variant: 'error' })
    } catch (error: any) {
      console.log(error)
      if (error instanceof AxiosError) {
        enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
      } else {
        enqueueSnackbar(error?.message, { variant: 'error' })
      }
    } finally {
      setIsUnregisterDialogOpen(false)
      setIsLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const verifyICCredentials = async (): Promise<void> => {
    setIsLoading(true)
    try {
      if (email === '' || email === undefined) {
        setIsVerified(false)
        return
      }

      const result = await getICStatus(email)
      if (result.success !== null || result.userAuth !== null) {
        setIsVerified(false)
      }
      setIsVerified(true)
    } catch (e: any) {
      // if (!isAuthorized(e)) await handleLogOut()
      setIsVerified(false)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchLinkedAccounts = async (): Promise<void> => {
    try {
      if (!email || !account?.accountId) return

      const params = {
        email,
        accountId: account.accountId
      }
      const result = await getAllLinkedAccounts(params)
      setLinkedAccounts(result?.data || [])
    } catch (error) {
      console.error('Failed to fetch linked accounts:', error)
      setLinkedAccounts([])
    }
  }

  useEffect(() => {
    if (!isInvoiceCloudEnabled) {
      setIsVerified(true)
      return
    }

    void verifyICCredentials()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  useEffect(() => {
    void fetchLinkedAccounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, account?.accountId])

  const handleUnlink = async (): Promise<void> => {
    setIsLoading(true)
    try {
      if (String(accountNumber) === account?.accountId) {
        throw new Error('Cannot unlink primary account')
      }
      if (account?.cognitoEmail) {
        const result = await unregisterICAccount({
          accountList: [String(accountNumber)],
          email: account.icEmail
        })

        const updateResponse = await APIs.updateInfo({
          accountId: account?.accountId ?? '',
          AccessToken: authToken,
          email: account?.ccsEmail,
          admin: true,
          addRemoveAcctUpdates: [
            {
              accountPropertyManager: account?.accountId ?? '',
              accountPropertyOwner: accountNumber ?? '',
              addRemoveAcctFlag: 'R'
            }
          ]
        }, authToken)

        console.log('updateResponse', updateResponse)

        if (updateResponse?.status !== 1) {
          throw new Error(updateResponse.message.message || 'Failed updating account')
        }

        if (isWEBclient) {
          enqueueSnackbar('Account unlinked successfully', { variant: 'success' })
          setAccountNumber('')
          void fetchLinkedAccounts()
          // Refresh account data to trigger re-render of LinkedAccounts component
          await getUser('', email ?? '')
          setIsUnlinkDialogOpen(false)
          setIsLoading(false)
          return
        }

        if (result.data?.data?.Data?.AccountsNotUnregistered[0]?.Message) {
          enqueueSnackbar(result.data?.data?.Data?.AccountsNotUnregistered[0]?.Message, { variant: 'error' })
          setIsUnlinkDialogOpen(false)
          setIsLoading(false)
          return
        }

        if (result.data?.data?.Data?.AccountsNotUnregistered?.length === 0) {
          enqueueSnackbar('Account unlinked successfully', { variant: 'success' })
          setAccountNumber('')
          void fetchLinkedAccounts()
          // Refresh account data to trigger re-render of LinkedAccounts component
          await getUser('', email ?? '')
        } else enqueueSnackbar('Failed to unlink account', { variant: 'error' })

        setIsUnlinkDialogOpen(false)
        setIsLoading(false)
      }
    } catch (error: any) {
      console.log('error', error)
      enqueueSnackbar(`Failed to unlink account: ${error ?? error.message}`, { variant: 'error', autoHideDuration: 3000 })
    } finally {
      setIsLoading(false)
    }
  }

  if (!account) {
    return (
      <Box border={`1px solid ${color.secondary.dark[300]}`} p={2}>
        <ProgressLoaders height="100%" />
      </Box>
    )
  }

  const tooltipMessage = (): string => {
    if (account?.errorMessage !== null) {
      return account?.errorMessage
    } else if (!account?.isRegisteredInInvoiceCloud && !isWEBclient) {
      return 'Account not linked to Invoice Cloud'
    } else if (!emailCorrect) {
      return 'Email address does not match'
    }
    return ''
  }

  const UnregisterButton = (): React.ReactElement => {
    return (
      <MButton
        variant="outlined"
        component="label"
        type="button"
        rounded="true"
        sx={{ fontSize: 14, display: !isWEBclient ? 'none' : 'block', textAlign: 'center' }}
        texttransform="none"
        fullWidth
        onClick={() => {
          setIsUnregisterDialogOpen(true)
        }}
      >
        Unregister Account
      </MButton>
    )
  }

  const LinkAccountButton = (): React.ReactElement => {
    return (
      <MButton
        fullWidth
        variant="outlined"
        component="label"
        disabled={hasErrors}
        type="button"
        rounded="true"
        texttransform="none"
        onClick={() => {
          setIsLinkDialogOpen(true)
        }}
        sx={{ fontSize: 14 }}
      >
        Link Account
      </MButton>
    )
  }

  const UnlinkAccountButton = (): React.ReactElement => {
    return (
      <MButton
        fullWidth
        variant="outlined"
        component="label"
        type="button"
        rounded="true"
        texttransform="none"
        disabled={!account?.isRegisteredInInvoiceCloud && !isWEBclient}
        onClick={() => {
          setIsUnlinkDialogOpen(true)
        }}
        sx={{ fontSize: 14 }}
      >
        Unlink Account
      </MButton>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={2}
      gap={1}
      bgcolor={color.grey[50]}
      border={`1px solid ${color.secondary.dark[300]}`}
    >
      <UnlinkConfirmDialog
        open={isUnlinkDialogOpen}
        onConfirm={handleUnlink}
        onClose={() => {
          setIsUnlinkDialogOpen(false)
          setAccountNumber('')
        }}
        isLoading={isLoading}
        accountNumber={accountNumber}
        setAccountNumber={setAccountNumber}
        primaryAccountId={account?.accountId}
        linkedAccounts={linkedAccounts}
      />

      <UnregisterConfirmDialog
        open={isUnregisterDialogOpen}
        onConfirm={handleUnregister}
        onClose={() => { setIsUnregisterDialogOpen(false) }}
        isLoading={isLoading}
      />

      <LinkAccount
        open={isLinkDialogOpen}
        accountId={account?.accountId}
        mode={LinkAccountMode.LINK}
        handleClose={() => { setIsLinkDialogOpen(false) }}
        onSuccess={() => {
          void fetchLinkedAccounts()
          if (onLinkedAccountsChange) {
            onLinkedAccountsChange()
          }
        }}
      />

      {account?.personName && (
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            !hasErrors
              ? (
                <Link to={customerLoginURL} target="_blank" rel="noopener noreferrer">
                  <VisibilityIcon
                    sx={{
                      color: color.primary[700],
                      backgroundColor: color.grey[50],
                      border: `1px solid ${color.secondary.dark[400]}`,
                      fontSize: 26,
                      borderRadius: '50%',
                      padding: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: color.primary[100]
                      }
                    }}
                  />
                </Link>
              )
              : (
                <Tooltip
                  title={tooltipMessage()}
                  arrow
                  placement="top">
                  <VisibilityOffIcon
                    sx={{
                      color: color.primary[700],
                      backgroundColor: color.grey[50],
                      border: `1px solid ${color.secondary.dark[400]}`,
                      fontSize: 26,
                      borderRadius: '50%',
                      padding: 1,
                      cursor: 'not-allowed'
                    }}
                  />
                </Tooltip>
              )
          }
        >
          <Avatar
            sx={{
              color: color.primary[900],
              backgroundColor: color.primary[100],
              border: `1px solid ${color.primary[100]}`,
              fontSize: 35,
              height: 80,
              width: 80
            }}
          >
            {stringAvatar(account?.personName)}
          </Avatar>
        </Badge>
      )}

      <Typography color={color.black} fontWeight={500} fontSize={18}>
        {account?.personName}
      </Typography>

       {ccsLink && ccsUnlink && unregisterAccountFeature
        ? (
          <Box display="flex" gap="5px" flexDirection={'column'} width="100%">
            <LinkAccountButton />
            <UnlinkAccountButton />
            <UnregisterButton />
          </Box>
        )
        : ccsLink && ccsUnlink
          ? (
            <Box display="flex" gap="5px" flexDirection={'column'} width="100%">
              <LinkAccountButton />
              <UnlinkAccountButton />
            </Box>
          )
          : unregisterAccountFeature
            ? (
              <Box display="flex" gap="5px" flexDirection={'column'} width="100%">
                <UnregisterButton />
              </Box>
            )
            : null}

      <Box>
        <Typography
          fontSize={14}
          fontWeight={400}
          color={color.grey[600]}
          display="inline"
        >
          Account ID: {' '}
        </Typography>
        <Typography
          fontSize={14}
          fontWeight={400}
          color={color.grey[700]}
          display="inline"
        >
          {account?.accountId}
        </Typography>
      </Box>
      <MButton
        variant="outlined"
        component="label"
        type="button"
        rounded="true"
        sx={{ fontSize: 14 }}
        texttransform="none"
        fullWidth
        onClick={() => {
          navigate(ROUTES_PATH.ACCOUNT_MANAGER)
        }}
      >
        Go back to Search
      </MButton>
    </Box>
  )
}

const stringAvatar = (name: string): string => {
  let firstLetter = ''
  let lastLetter = ''
  const splitName = name.split(',')
  if (splitName[0]) {
    if (splitName[0][0]) {
      firstLetter = splitName[0][0] || ''
    }
  }

  if (splitName[1]) {
    if (splitName[1][0]) {
      lastLetter = splitName[1][0] || ''
    }
  }

  return `${firstLetter}${lastLetter}`
}
